import React from 'react';
import '../QuizTaker.css';
import { QuizQuestion } from '../../utils/QuizQuestionSelector';

interface QuizConfig {
  seed: number;
  questionCount: number;
  chapter: string;
  book: string;
  questionType: 'true_false' | 'multiple_choice';
}

interface QuizStats {
  totalAnswered: number;
  totalCorrect: number;
  totalIncorrect: number;
  accuracy: number;
  studyBankCount: number;
}

interface QuizTakerProps {
  quizQuestions: QuizQuestion[];
  currentQuestionIndex: number;
  userAnswers: Record<string, string>;
  showFeedback: boolean;
  currentFeedback: { isCorrect: boolean; message: string; explanation: string } | null;
  quizConfig: QuizConfig;
  stats: QuizStats;
  bookTitle: string;
  onAnswer: (answer: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onConfigureQuiz: () => void;
}

const QuizTaker: React.FC<QuizTakerProps> = ({
  quizQuestions,
  currentQuestionIndex,
  userAnswers,
  showFeedback,
  currentFeedback,
  quizConfig,
  stats,
  bookTitle,
  onAnswer,
  onNext,
  onPrevious,
  onSkip,
  onConfigureQuiz
}) => {
  const currentQuestion = quizQuestions[currentQuestionIndex];

  if (!currentQuestion) {
    return (
      <div className="content-panel">
        <div className="no-questions">
          <h2>No Questions Available</h2>
          <p>No questions found for the current configuration.</p>
          <button onClick={onConfigureQuiz} className="btn btn-primary">
            Configure Quiz
          </button>
        </div>
      </div>
    );
  }

  const userAnswer = userAnswers[currentQuestion.id];
  const isAnswered = !!userAnswer && userAnswer !== 'skipped';

  return (
    <div className="content-container">
      <div className="glass-header">
        <div className="quiz-info">
          <h2>{bookTitle} Quiz</h2>
          <div className="quiz-meta">
            <span>Question {currentQuestionIndex + 1} of {quizQuestions.length}</span>
            <span>Seed: {quizConfig.seed}</span>
          </div>
        </div>

        <div className="progress-overview">
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-label">Total Progress:</span>
              <span className="stat-value">{stats.totalAnswered} answered</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Correct:</span>
              <span className="stat-value correct">{stats.totalCorrect}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Accuracy:</span>
              <span className="stat-value">{stats.accuracy}%</span>
            </div>
          </div>
        </div>

        <div className="header-buttons">
          <button onClick={onConfigureQuiz} className="btn btn-secondary btn-small">
            Configure
          </button>
        </div>
      </div>

      <div className="content-panel question-container">
        <div className="question-text">
          <h3>{currentQuestion.question}</h3>
        </div>

        <div className="answer-options">
          {currentQuestion.type === 'true_false' ? (
            <>
              <button
                className={`answer-option ${userAnswer === 'true' ? 'selected' : ''}`}
                onClick={() => onAnswer('true')}
                disabled={isAnswered}
              >
                True
              </button>
              <button
                className={`answer-option ${userAnswer === 'false' ? 'selected' : ''}`}
                onClick={() => onAnswer('false')}
                disabled={isAnswered}
              >
                False
              </button>
            </>
          ) : (
            currentQuestion.options?.map((option, index) => (
              <button
                key={index}
                className={`answer-option ${userAnswer === option ? 'selected' : ''}`}
                onClick={() => onAnswer(option)}
                disabled={isAnswered}
              >
                {option}
              </button>
            ))
          )}
        </div>

        {showFeedback && currentFeedback && (
          <div className={`feedback ${currentFeedback.isCorrect ? 'correct' : 'incorrect'}`}>
            <h4>{currentFeedback.isCorrect ? 'Correct!' : 'Incorrect!'}</h4>
            <p>{currentFeedback.message}</p>
            {currentFeedback.explanation && (
              <p><strong>Explanation:</strong> {currentFeedback.explanation}</p>
            )}
          </div>
        )}

        <div className="quiz-navigation">
          <button
            onClick={onPrevious}
            disabled={currentQuestionIndex === 0}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          <button
            onClick={onSkip}
            disabled={isAnswered}
            className="btn btn-secondary"
          >
            Skip
          </button>
          
          <button
            onClick={onNext}
            disabled={!isAnswered}
            className="btn btn-primary"
          >
            {currentQuestionIndex === quizQuestions.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizTaker; 